/**
 * Nebraska Home Health Agency State-Specific Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK:
 * - Primary State Regulation: 175 NAC 14 (Home Health Agencies)
 * - Cross-Reference: 175 NAC 1 (General Provisions)
 * - Statutory Authority: Neb. Rev. Stat. 71-401 et seq. (Health Care Facility Licensure Act)
 * - Federal Standards: 42 CFR 484 (Medicare CoPs - for certified agencies)
 * - Medication Aides: 172 NAC 95-96
 * 
 * KEY VERIFIED REQUIREMENTS (December 2025):
 * - License expires: January 31st each year per 175 NAC 14-005
 * - Renewal fees (tiered by patient volume):
 *   - 1-50 admissions: $650
 *   - 51-200 admissions: $850
 *   - 201+ admissions: $950
 * - Must provide skilled nursing OR minimum 1 therapeutic service
 * - Supervising RN available during all service hours
 * - Administrator: RN, NHA, or bachelor's + 2 years experience
 * - Plan of care review every 62 days
 * - Background checks: Criminal, Nurse Aide Registry, Sex Offender Registry
 * - Regulation amended May 5, 2024
 * 
 * Regulatory Body: Nebraska Department of Health and Human Services (DHHS)
 * Division of Public Health, Licensure Unit
 * 
 * Last Verified: December 2025 against Nebraska Rules and DHHS sources
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const NEBRASKA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'NE-A1',
    itemId: 'A1',
    stateCode: 'NE',
    title: 'DHHS Home Health Agency License Status',
    description: 'Nebraska requires all home health agencies to be licensed by DHHS under 175 NAC 14',
    severity: 'high',
    regulationCode: '175 NAC 14-003',
    regulationName: 'Licensure',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from Nebraska DHHS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Nebraska home health licenses expire January 31st each year per 175 NAC 14-005.',
      },
      {
        id: 'q2',
        question: 'What is your license expiration date?',
        inputType: 'date',
        helpText: 'All Nebraska HHA licenses expire January 31st annually.',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your DHHS Home Health Agency license is current and active per 175 NAC 14.',
      atRisk: 'Your license expires soon. Nebraska licenses expire January 31st. Submit renewal application with fee to DHHS before expiration.',
      nonCompliant: 'Operating a home health agency in Nebraska without a valid DHHS license violates 175 NAC 14 and the Health Care Facility Licensure Act. Contact DHHS immediately at (402) 471-4967.',
      unknown: 'Verify your license status with DHHS Licensure Unit at (402) 471-4967.',
    },
    stateNotes: [
      'All licenses expire January 31st each year',
      'Renewal fees tiered by patient volume: $650 / $850 / $950',
      'Initial licensure requires on-site survey',
      'Agency may not operate without active license',
    ],
  },

  {
    id: 'NE-A2',
    itemId: 'A2',
    stateCode: 'NE',
    title: 'Administrator Qualifications',
    description: 'Nebraska requires qualified administrator for all home health agencies',
    severity: 'high',
    regulationCode: '175 NAC 14-010.02',
    regulationName: 'Administrator',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency administrator meet ONE of the following DHHS qualification requirements?',
        inputType: 'select',
        options: [
          { value: 'rn', label: 'Current Nebraska RN license (or Nurse Licensure Compact authority)' },
          { value: 'nha', label: 'Current Nebraska Nursing Home Administrator license' },
          { value: 'degree_exp', label: 'Bachelor\'s degree in healthcare field + 2 years home health experience' },
          { value: 'none', label: 'None of the above' },
        ],
        required: true,
        helpText: 'Per 175 NAC 14-010.02(A): Administrator must meet one of the specified qualification pathways.',
      },
      {
        id: 'q2',
        question: 'Is a backup administrator designated in writing?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Both administrator and backup administrator must be prepared for DHHS inspection.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'none', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['rn', 'nha', 'degree_exp'] },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Administrator qualifications meet DHHS requirements per 175 NAC 14-010.02.',
      atRisk: 'Designate a backup administrator in writing. Both must be prepared for DHHS inspection.',
      nonCompliant: 'Administrator does not meet DHHS qualification requirements under 175 NAC 14-010.02.',
      unknown: 'Verify administrator qualifications against 175 NAC 14-010.02 requirements.',
    },
    stateNotes: [
      'Administrator must be RN, NHA, or bachelor\'s + 2 years experience',
      'Backup administrator required',
      'Both must demonstrate compliance during DHHS inspection',
    ],
  },

  {
    id: 'NE-A3',
    itemId: 'A3',
    stateCode: 'NE',
    title: 'Services and Staffing Requirements',
    description: 'Nebraska requires specific services and RN supervision',
    severity: 'high',
    regulationCode: '175 NAC 14-003.03',
    regulationName: 'Services',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Which services does your agency provide? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'nursing', label: 'Skilled Nursing Care' },
          { value: 'pt', label: 'Physical Therapy' },
          { value: 'st', label: 'Speech Pathology' },
          { value: 'ot', label: 'Occupational Therapy' },
          { value: 'rt', label: 'Respiratory Care' },
          { value: 'hha', label: 'Home Health Aide Services' },
          { value: 'msw', label: 'Social Work Services' },
          { value: 'iv', label: 'Intravenous Therapy' },
          { value: 'dialysis', label: 'Dialysis Services' },
        ],
        required: true,
        helpText: 'Per 175 NAC 14-003.03(A): Must provide skilled nursing OR at least 1 therapeutic service.',
      },
      {
        id: 'q2',
        question: 'Is a supervising RN available or on-call during ALL hours that skilled nursing or home health aide services are provided?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 175 NAC 14-003.03(E): Supervising RN must be available during all service hours.',
      },
    ],
    scoringRules: [
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 1 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Services and staffing meet 175 NAC 14-003.03 requirements.',
      atRisk: 'Review service offerings and RN supervision coverage.',
      nonCompliant: 'Agency must provide skilled nursing or therapeutic services AND have RN supervision per 175 NAC 14-003.03.',
      unknown: 'Verify services and staffing against 175 NAC 14-003.03.',
    },
    stateNotes: [
      'Must provide skilled nursing OR minimum 1 therapeutic service',
      'Therapeutic services: PT, ST, OT, RT, HHA, MSW, IV, dialysis',
      'Supervising RN available during ALL service hours',
      'Services must be provided per practitioner orders',
    ],
  },

  {
    id: 'NE-A4',
    itemId: 'A4',
    stateCode: 'NE',
    title: 'Written Contracts for Arranged Services',
    description: 'Nebraska requires written contracts when services are provided under arrangement',
    severity: 'medium',
    regulationCode: '175 NAC 14-003.03(D)',
    regulationName: 'Contracts',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide any services through arrangement with another entity, agency, or individual?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - all services provided by agency employees' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are ALL arranged services governed by written contracts conforming to 175 NAC 14 requirements?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Contract requirements met per 175 NAC 14-003.03(D).',
      atRisk: 'Review contracts for arranged services.',
      nonCompliant: 'All arranged services must have written contracts per 175 NAC 14-003.03(D).',
      unknown: 'Verify contract documentation.',
    },
    stateNotes: [
      'Services under arrangement must have written contracts',
      'Contracts must conform to 175 NAC 14 requirements',
      'Agency retains responsibility for quality of contracted services',
      'Document contractor qualifications and insurance',
    ],
  },

  {
    id: 'NE-A5',
    itemId: 'A5',
    stateCode: 'NE',
    title: 'Business Entity Registration',
    description: 'Nebraska requires proper business registration with Secretary of State',
    severity: 'high',
    regulationCode: 'Neb. Rev. Stat. 21-301',
    regulationName: 'Business Entity',
    agencyName: 'Nebraska Secretary of State',
    agencyAbbr: 'SOS',
    agencyPhone: '(402) 471-4079',
    agencyUrl: 'https://sos.nebraska.gov/business-services',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency properly registered with the Nebraska Secretary of State?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your biennial report current?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Unsure' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Business entity registration is current with Nebraska Secretary of State.',
      atRisk: 'Verify biennial report is current with Secretary of State.',
      nonCompliant: 'Agency must be properly registered in Nebraska.',
      unknown: 'Verify business registration with Nebraska Secretary of State.',
    },
    stateNotes: [
      'Biennial reports required to maintain good standing',
      'Check status at sos.nebraska.gov',
      'Contact Secretary of State at (402) 471-4079',
      'Keep registered agent information current',
    ],
  },

  {
    id: 'NE-A6',
    itemId: 'A6',
    stateCode: 'NE',
    title: 'Medicare Certification Status',
    description: 'Nebraska home health agencies may obtain Medicare certification',
    severity: 'medium',
    regulationCode: '42 CFR 484',
    regulationName: 'Medicare CoPs',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(800) 245-5832',
    agencyUrl: 'https://www.cms.gov/center/hha.asp',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide Medicare-certified home health services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Medicare certification requires state license first, then meeting 42 CFR 484 standards.',
      },
      {
        id: 'q2',
        question: 'Is your Medicare certification current and in good standing?',
        inputType: 'select',
        options: [
          { value: 'current', label: 'Yes, current with no deficiencies' },
          { value: 'deficiencies', label: 'Current but with deficiencies' },
          { value: 'expired', label: 'Expired or suspended' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'current', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'deficiencies', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'expired', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medicare certification status is satisfactory.',
      atRisk: 'Address Medicare survey deficiencies promptly.',
      nonCompliant: 'Medicare certification has expired or been suspended. Cannot bill Medicare.',
      unknown: 'Verify Medicare certification status.',
    },
    stateNotes: [
      'State license required before Medicare certification',
      'Medicare hotline: (800) 245-5832',
      'OASIS transmission required for certified agencies',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL & BACKGROUND CHECKS
  // ============================================
  {
    id: 'NE-B1',
    itemId: 'B1',
    stateCode: 'NE',
    title: 'Criminal Background Checks',
    description: 'Nebraska requires criminal background checks for direct care staff',
    severity: 'high',
    regulationCode: '175 NAC 14-010.04(A)(i)',
    regulationName: 'Background Checks',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do you complete criminal background checks for ALL direct care staff before they have unsupervised patient contact?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before unsupervised contact' },
          { value: 'sometimes', label: 'Sometimes staff start before clearance' },
          { value: 'no', label: 'No background checks' },
        ],
        required: true,
        helpText: 'Per 175 NAC 14-010.04(A)(i): Criminal background checks must be completed before staff have unsupervised patient contact.',
      },
      {
        id: 'q2',
        question: 'Are background checks completed through a governmental law enforcement agency or approved private entity?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Unsure' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Criminal background check procedures meet 175 NAC 14-010.04 requirements.',
      atRisk: 'Background check documentation may be incomplete.',
      nonCompliant: 'Staff working before background check clearance or lack of background checks violates 175 NAC 14-010.04(A)(i). Implement immediately.',
      unknown: 'Verify background check procedures.',
    },
    stateNotes: [
      'Checks required before unsupervised patient contact',
      'Must use governmental agency or approved private entity',
      'Documentation maintained for employment duration',
      'Nebraska State Patrol can provide background checks',
      'Keep results in personnel file',
    ],
  },

  {
    id: 'NE-B2',
    itemId: 'B2',
    stateCode: 'NE',
    title: 'Nebraska Registry Checks',
    description: 'Nebraska requires verification against multiple state registries',
    severity: 'high',
    regulationCode: '175 NAC 14-010.04(A)(ii)',
    regulationName: 'Registry Checks',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Which Nebraska registries do you check for ALL direct care staff before hire?',
        inputType: 'checklist',
        options: [
          { value: 'nurse_aide', label: 'Nurse Aide Registry' },
          { value: 'sex_offender', label: 'Sex Offender Registry' },
          { value: 'central_registry', label: 'Central Registry of Child Abuse/Neglect' },
          { value: 'adult_ps', label: 'Adult Protective Services Registry' },
        ],
        required: true,
        helpText: 'Per 175 NAC 14-010.04(A)(ii): Pre-employment registry checks required. Cannot employ with adverse findings on Sex Offender or Nurse Aide Registry.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Registry check procedures meet 175 NAC 14-010.04(A)(ii) requirements.',
      atRisk: 'Check all required Nebraska registries before hire.',
      nonCompliant: 'Registry checks are required per 175 NAC 14-010.04(A)(ii). Cannot employ with Sex Offender or Nurse Aide Registry adverse findings.',
      unknown: 'Verify registry check procedures.',
    },
    stateNotes: [
      'CANNOT employ with adverse Sex Offender Registry findings',
      'CANNOT employ with adverse Nurse Aide Registry findings',
      'Must also check similar registries in states where person resided in past 10 years',
    ],
  },

  {
    id: 'NE-B3',
    itemId: 'B3',
    stateCode: 'NE',
    title: 'OIG/SAM Exclusion Verification',
    description: 'Federal exclusion list verification required',
    severity: 'high',
    regulationCode: '42 CFR 1001.1901',
    regulationName: 'OIG Exclusions',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://oig.hhs.gov/exclusions/',
    questions: [
      {
        id: 'q1',
        question: 'How often do you check the OIG exclusion list for employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'Not checking' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you check SAM.gov exclusions?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'monthly' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG/SAM exclusion verification is performed regularly.',
      atRisk: 'Implement monthly OIG/SAM verification. At-hire only is insufficient.',
      nonCompliant: 'Employing excluded individuals can result in civil monetary penalties.',
      unknown: 'Verify OIG/SAM checking procedures.',
    },
    stateNotes: [
      'OIG recommends monthly verification',
      'SAM.gov verification also recommended',
      'Document all exclusion checks',
      'OIG LEIE: https://exclusions.oig.hhs.gov',
      'SAM.gov: https://sam.gov',
    ],
  },

  {
    id: 'NE-B4',
    itemId: 'B4',
    stateCode: 'NE',
    title: 'Professional License Verification',
    description: 'Nebraska requires verification of professional licenses',
    severity: 'high',
    regulationCode: '175 NAC 14-010.04',
    regulationName: 'Licenses',
    agencyName: 'Nebraska Board of Nursing',
    agencyAbbr: 'BON',
    agencyPhone: '(402) 471-4376',
    agencyUrl: 'https://dhhs.ne.gov/licensure',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all professional licenses (RN, LPN, PT, OT, ST, RT) with the appropriate licensing board?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before hire and tracked for renewal' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No formal verification' },
        ],
        required: true,
        helpText: 'Licensed staff must hold active Nebraska credentials or Nurse Licensure Compact authority.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification is comprehensive and ongoing.',
      atRisk: 'Track license renewals. At-hire only verification is insufficient.',
      nonCompliant: 'Professional license verification is required. Implement immediately.',
      unknown: 'Verify license verification procedures are in place.',
    },
    stateNotes: [
      'Nebraska participates in Nurse Licensure Compact',
      'Verify licenses at hire and track renewals',
      'RNs must hold active credential or NLC authority',
      'Nebraska Board of Nursing: (402) 471-4376',
      'Verify at dhhs.ne.gov/licensure',
    ],
  },

  {
    id: 'NE-B5',
    itemId: 'B5',
    stateCode: 'NE',
    title: 'Personnel File Requirements',
    description: 'Nebraska requires specific documents in each employee\'s personnel file',
    severity: 'high',
    regulationCode: '175 NAC 14-010.04',
    regulationName: 'Personnel',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Which documents do you maintain in EVERY employee\'s personnel file?',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'license', label: 'Professional license verification' },
          { value: 'background', label: 'Criminal background check results' },
          { value: 'registry', label: 'Nebraska registry check results' },
          { value: 'oig', label: 'OIG/SAM exclusion verification' },
          { value: 'competency', label: 'Competency evaluation' },
          { value: 'orientation', label: 'Orientation documentation' },
          { value: 'health', label: 'Health screening documentation' },
          { value: 'job_desc', label: 'Signed job description' },
          { value: 'i9', label: 'I-9 Employment Eligibility' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain required documentation per 175 NAC 14.',
      atRisk: 'Some personnel files may be missing required documents. Missing: {missing}.',
      nonCompliant: 'Personnel files are missing critical documentation. Immediately obtain: {missing}.',
      unknown: 'Audit personnel files against 175 NAC 14 requirements.',
    },
    stateNotes: [
      'Documentation maintained for employment duration',
      'Background/registry checks must be completed before unsupervised contact',
      'DHHS may review personnel files during inspections',
    ],
  },

  // ============================================
  // SECTION C: HOME HEALTH AIDE REQUIREMENTS
  // ============================================
  {
    id: 'NE-C1',
    itemId: 'C1',
    stateCode: 'NE',
    title: 'Home Health Aide Qualifications',
    description: 'Nebraska requires home health aides to meet specific qualifications',
    severity: 'high',
    regulationCode: '175 NAC 14-010.04(G)',
    regulationName: 'HHA Qualifications',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL home health aides meet Nebraska qualification requirements?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HHAs are qualified per Neb. Rev. Stat. 71-6603' },
          { value: 'most', label: 'Most are qualified' },
          { value: 'no', label: 'No / Unsure' },
        ],
        required: true,
        helpText: 'Per 175 NAC 14-010.04(G)(i): Must use only home health aides qualified per Nebraska statute.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All home health aides meet Nebraska qualification requirements.',
      atRisk: 'Verify ALL home health aides meet qualification requirements.',
      nonCompliant: 'Home health aides must be qualified per 175 NAC 14-010.04(G) and Neb. Rev. Stat. 71-6603.',
      unknown: 'Verify HHA qualifications against Nebraska requirements.',
    },
    stateNotes: [
      'HHAs must be qualified per Neb. Rev. Stat. 71-6603',
      '75-hour training required for nurse aides',
      'Competency evaluation required',
      'Check Nurse Aide Registry before hire',
      'Cannot employ with adverse registry findings',
    ],
  },

  {
    id: 'NE-C2',
    itemId: 'C2',
    stateCode: 'NE',
    title: 'Medication Aide Requirements',
    description: 'Nebraska requires medication aides to be registered',
    severity: 'high',
    regulationCode: '175 NAC 14-010.07(A)(iii)',
    regulationName: 'Medication Aides',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Medication-Aide.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency use persons other than licensed health care professionals to provide medications?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - only licensed professionals administer medications' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are ALL such persons registered medication aides compliant with 172 NAC 95 and 96?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medication provision meets 175 NAC 14-010.07 requirements.',
      atRisk: 'Verify medication aide registrations.',
      nonCompliant: 'Persons providing medications must be registered medication aides per 175 NAC 14-010.07(A)(iii) and 172 NAC 95-96.',
      unknown: 'Verify medication aide status.',
    },
    stateNotes: [
      'Must use registered medication aides only',
      'Comply with Medication Aide Act and 172 NAC 95-96',
      'Report adverse reactions and medication errors immediately',
      'Verify registration at dhhs.ne.gov/licensure',
    ],
  },

  // ============================================
  // SECTION D: CARE DELIVERY
  // ============================================
  {
    id: 'NE-D1',
    itemId: 'D1',
    stateCode: 'NE',
    title: 'Plan of Care Requirements',
    description: 'Nebraska requires physician-ordered plans of care with specific review intervals',
    severity: 'high',
    regulationCode: '175 NAC 14-010.04(E)',
    regulationName: 'Plan of Care',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Are all patients on a practitioner-ordered plan of care before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often does an RN review the plan of care?',
        inputType: 'select',
        options: [
          { value: 'every_62', label: 'At least every 62 days (or as condition requires)' },
          { value: 'every_90', label: 'Every 90 days' },
          { value: 'rarely', label: 'Rarely or as needed' },
        ],
        required: true,
        helpText: 'Per 175 NAC 14-010.04(E)(1): RN must review plan of care at least every 62 days.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'every_62' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'equals', questionId: 'q2', value: 'every_90' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plan of care processes meet 175 NAC 14-010.04(E) requirements.',
      atRisk: 'POC review must be at least every 62 days per 175 NAC 14-010.04(E)(1).',
      nonCompliant: 'Services must not begin without practitioner-ordered plan of care.',
      unknown: 'Review care plan requirements under 175 NAC 14-010.04(E).',
    },
    stateNotes: [
      'Practitioner order required before services begin',
      'RN reviews POC every 62 days minimum',
      'Review more frequently as condition requires',
      'Document all POC updates with practitioner signature',
      'Patient/family participate in care planning',
    ],
  },

  {
    id: 'NE-D2',
    itemId: 'D2',
    stateCode: 'NE',
    title: 'RN Supervision of Services',
    description: 'Nebraska requires RN supervision during service hours',
    severity: 'high',
    regulationCode: '175 NAC 14-003.03(E)',
    regulationName: 'RN Supervision',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Is a supervising RN available or on-call during ALL hours when skilled nursing care or home health aide services are provided?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always available or on-call' },
          { value: 'usually', label: 'Usually, but not always' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN supervision meets 175 NAC 14-003.03(E) requirements.',
      atRisk: 'RN must be available during ALL service hours, not just most.',
      nonCompliant: 'Supervising RN must be available during all service hours per 175 NAC 14-003.03(E).',
      unknown: 'Verify RN supervision coverage.',
    },
    stateNotes: [
      'RN must be available during ALL service hours',
      'Applies to skilled nursing and HHA services',
      'On-call coverage is acceptable',
      'Document RN availability and on-call schedule',
    ],
  },

  {
    id: 'NE-D3',
    itemId: 'D3',
    stateCode: 'NE',
    title: 'Patient Rights',
    description: 'Nebraska requires providing patient rights information',
    severity: 'high',
    regulationCode: '175 NAC 14-010.05',
    regulationName: 'Rights',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written notice of patient rights before or during the initial visit?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does the patient rights notice include DHHS complaint information?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / Unsure' },
        ],
        helpText: 'Medicare Hotline: (800) 245-5832',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights notice meets requirements.',
      atRisk: 'Include DHHS complaint hotline information in patient rights.',
      nonCompliant: 'Written patient rights notice is required.',
      unknown: 'Review patient rights requirements.',
    },
    stateNotes: [
      'Provide written rights notice at admission',
      'Include DHHS/Medicare complaint information',
      'Medicare Home Health Hotline: (800) 245-5832',
    ],
  },

  // ============================================
  // SECTION E: DOCUMENTATION & RECORDS
  // ============================================
  {
    id: 'NE-E1',
    itemId: 'E1',
    stateCode: 'NE',
    title: 'Clinical Records',
    description: 'Nebraska requires complete clinical documentation',
    severity: 'high',
    regulationCode: '175 NAC 14-008',
    regulationName: 'Records',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do your clinical records contain all required elements?',
        inputType: 'checklist',
        options: [
          { value: 'consent', label: 'Signed consent for treatment' },
          { value: 'assessment', label: 'Initial assessment' },
          { value: 'poc', label: 'Practitioner-signed plan of care' },
          { value: 'notes', label: 'Visit notes for each service' },
          { value: 'orders', label: 'Practitioner orders' },
          { value: 'communication', label: 'Communication with practitioner' },
          { value: 'discharge', label: 'Discharge summary' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records contain required elements per 175 NAC 14-008.',
      atRisk: 'Clinical records may be missing required elements.',
      nonCompliant: 'Clinical records are incomplete. This is a common survey deficiency.',
      unknown: 'Audit clinical records against 175 NAC 14-008 requirements.',
    },
    stateNotes: [
      'DHHS reviews clinical records during inspections',
      'Maintain complete documentation per 175 NAC 14-008',
      'Document all practitioner communication',
    ],
  },

  {
    id: 'NE-E2',
    itemId: 'E2',
    stateCode: 'NE',
    title: 'Record Retention',
    description: 'Nebraska requires minimum 5-year record retention',
    severity: 'medium',
    regulationCode: '175 NAC 1-005',
    regulationName: 'Retention',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient and employee records?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '5plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '3to4', result: 'AT_RISK' },
      { condition: 'includes', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets Nebraska\'s 5-year requirement.',
      atRisk: 'Extend retention period to meet 5-year requirement.',
      nonCompliant: 'Records must be retained for at least 5 years.',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      'Retain records for minimum 5 years',
      'Some records may require longer retention (minors, Medicare)',
    ],
  },

  // ============================================
  // SECTION F: HEALTH & SAFETY
  // ============================================
  {
    id: 'NE-F1',
    itemId: 'F1',
    stateCode: 'NE',
    title: 'Staff Health Status',
    description: 'Nebraska requires maintaining staff health to prevent disease transmission',
    severity: 'high',
    regulationCode: '175 NAC 14-010.04(B)',
    regulationName: 'Staff Health',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain staff health status to prevent potential disease transmission?',
        inputType: 'checklist',
        options: [
          { value: 'health_screening', label: 'Pre-employment health screening' },
          { value: 'tb', label: 'TB screening' },
          { value: 'illness_policy', label: 'Policy for staff working while ill' },
          { value: 'immunizations', label: 'Immunization tracking' },
        ],
        required: true,
        helpText: 'Per 175 NAC 14-010.04(B): Maintain staff health to prevent disease transmission.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Staff health maintenance meets 175 NAC 14-010.04(B) requirements.',
      atRisk: 'Strengthen staff health monitoring procedures.',
      nonCompliant: 'Staff health maintenance is required per 175 NAC 14-010.04(B).',
      unknown: 'Review staff health requirements.',
    },
    stateNotes: [
      'Prevent disease transmission to patients and staff',
      'Document health screenings',
      'Have policy for staff working while ill',
    ],
  },

  {
    id: 'NE-F2',
    itemId: 'F2',
    stateCode: 'NE',
    title: 'Infection Control Program',
    description: 'Nebraska requires infection control procedures',
    severity: 'high',
    regulationCode: '175 NAC 14-010.06',
    regulationName: 'Infection Control',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Which infection control elements does your agency have in place?',
        inputType: 'checklist',
        options: [
          { value: 'policy', label: 'Written infection control policy' },
          { value: 'training', label: 'Staff infection control training' },
          { value: 'supplies', label: 'Adequate PPE supplies' },
          { value: 'handwashing', label: 'Hand hygiene protocols' },
          { value: 'reporting', label: 'Infection reporting procedures' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets requirements.',
      atRisk: 'Strengthen infection control program.',
      nonCompliant: 'Infection control program is inadequate.',
      unknown: 'Review infection control requirements.',
    },
    stateNotes: [
      'Written policies required',
      'Train all staff on infection control',
      'Provide adequate PPE',
    ],
  },

  {
    id: 'NE-F3',
    itemId: 'F3',
    stateCode: 'NE',
    title: 'Emergency Preparedness',
    description: 'Nebraska requires emergency preparedness planning',
    severity: 'medium',
    regulationCode: '175 NAC 14-010',
    regulationName: 'Emergency',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have an emergency preparedness plan that includes:',
        inputType: 'checklist',
        options: [
          { value: 'plan', label: 'Written emergency/disaster plan' },
          { value: 'weather', label: 'Severe weather procedures (tornadoes, blizzards)' },
          { value: 'contact', label: 'Emergency contact procedures' },
          { value: 'communication', label: 'Staff communication plan' },
          { value: 'training', label: 'Staff training on emergency procedures' },
        ],
        required: true,
        helpText: 'Nebraska has unique severe weather considerations including tornadoes and blizzards.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan is comprehensive.',
      atRisk: 'Strengthen emergency preparedness plan.',
      nonCompliant: 'Emergency preparedness plan is inadequate.',
      unknown: 'Review emergency preparedness requirements.',
    },
    stateNotes: [
      'Address Nebraska severe weather (tornadoes, blizzards)',
      'Train staff on emergency procedures',
      'Document emergency drills',
    ],
  },

  // ============================================
  // SECTION G: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'NE-G1',
    itemId: 'G1',
    stateCode: 'NE',
    title: 'Abuse Recognition and Reporting Training',
    description: 'Nebraska requires abuse recognition and mandatory reporter training',
    severity: 'high',
    regulationCode: 'Neb. Rev. Stat. 28-372',
    regulationName: 'Abuse Reporting',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(800) 652-1999',
    agencyUrl: 'https://dhhs.ne.gov/APS',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL employees receive training on abuse, neglect, and exploitation recognition and reporting?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees at orientation and annually' },
          { value: 'orientation', label: 'At orientation only' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
        helpText: 'Nebraska healthcare workers are mandatory reporters of abuse/neglect.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'orientation', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Abuse recognition training is comprehensive and ongoing.',
      atRisk: 'Provide abuse training annually, not just at orientation.',
      nonCompliant: 'Abuse recognition training is required. Nebraska healthcare workers are mandatory reporters.',
      unknown: 'Verify abuse training requirements.',
    },
    stateNotes: [
      'Nebraska healthcare workers are mandatory reporters',
      'APS hotline: (800) 652-1999',
      'Train all staff at orientation and annually',
    ],
  },

  {
    id: 'NE-G2',
    itemId: 'G2',
    stateCode: 'NE',
    title: 'HIPAA Training',
    description: 'Federal HIPAA training requirements',
    severity: 'high',
    regulationCode: '45 CFR 164',
    regulationName: 'HIPAA',
    agencyName: 'U.S. Department of Health and Human Services',
    agencyAbbr: 'HHS',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL employees receive HIPAA training?',
        inputType: 'select',
        options: [
          { value: 'initial_annual', label: 'Yes, at hire and annually' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'initial_annual', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA training is provided at hire and annually.',
      atRisk: 'HIPAA training should be provided annually, not just at hire.',
      nonCompliant: 'HIPAA training is required by federal law.',
      unknown: 'Verify HIPAA training requirements.',
    },
    stateNotes: [
      'Annual HIPAA training recommended',
      'Include privacy and security components',
      'Document all training',
    ],
  },

  {
    id: 'NE-G3',
    itemId: 'G3',
    stateCode: 'NE',
    title: 'CPR/First Aid Certification',
    description: 'CPR certification requirements for direct care staff',
    severity: 'high',
    regulationCode: '175 NAC 14-010.04',
    regulationName: 'CPR/First Aid',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL direct care staff have current CPR certification?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have current certification' },
          { value: 'most', label: 'Most staff, some may be expired' },
          { value: 'no', label: 'No / Not tracking' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All direct care staff have current CPR certification.',
      atRisk: 'Verify and renew any expired CPR certifications.',
      nonCompliant: 'Direct care staff must have current CPR certification.',
      unknown: 'Verify CPR certification status for all direct care staff.',
    },
    stateNotes: [
      'Track CPR certification expiration dates',
      'Ensure renewals are completed before expiration',
    ],
  },

  // ============================================
  // SECTION H: QUALITY & COMPLIANCE
  // ============================================
  {
    id: 'NE-H1',
    itemId: 'H1',
    stateCode: 'NE',
    title: 'DHHS Inspection Readiness',
    description: 'Preparation for DHHS compliance inspections',
    severity: 'high',
    regulationCode: '175 NAC 14-004',
    regulationName: 'Inspections',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'When was your last DHHS inspection?',
        inputType: 'select',
        options: [
          { value: 'recent', label: 'Within the last 12 months' },
          { value: 'over_12', label: 'More than 12 months ago' },
          { value: 'never', label: 'Never inspected / New agency' },
        ],
        required: true,
        helpText: 'DHHS may conduct unannounced inspections at any time per 175 NAC 14-004.',
      },
      {
        id: 'q2',
        question: 'Were any deficiencies cited in your last inspection?',
        inputType: 'select',
        options: [
          { value: 'none', label: 'No deficiencies' },
          { value: 'minor', label: 'Minor deficiencies (corrected)' },
          { value: 'major', label: 'Major deficiencies' },
          { value: 'na', label: 'N/A - not yet inspected' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['recent', 'over_12'] },
        { condition: 'includes', questionId: 'q2', value: ['none', 'minor'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'major', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'never', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Inspection history is satisfactory.',
      atRisk: 'Address any outstanding deficiencies. Prepare for unannounced inspections.',
      nonCompliant: 'Major deficiencies must be corrected.',
      unknown: 'Contact DHHS to verify inspection status.',
    },
    stateNotes: [
      'DHHS may conduct unannounced inspections anytime',
      'Initial licensure requires scheduled inspection',
      'Maintain compliance with 175 NAC 14-010',
    ],
  },

  {
    id: 'NE-H2',
    itemId: 'H2',
    stateCode: 'NE',
    title: 'Quality Assurance Program',
    description: 'Nebraska requires quality assurance activities',
    severity: 'medium',
    regulationCode: '175 NAC 14-010',
    regulationName: 'QA Program',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(402) 471-4967',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a quality assurance program that includes:',
        inputType: 'checklist',
        options: [
          { value: 'written', label: 'Written QA plan' },
          { value: 'review', label: 'Regular clinical record reviews' },
          { value: 'outcomes', label: 'Patient outcome tracking' },
          { value: 'complaints', label: 'Complaint tracking and resolution' },
          { value: 'improvement', label: 'Performance improvement activities' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality assurance program is comprehensive.',
      atRisk: 'Strengthen quality assurance activities.',
      nonCompliant: 'Quality assurance program is inadequate.',
      unknown: 'Review quality assurance requirements.',
    },
    stateNotes: [
      'Document all QA activities',
      'Track and trend outcomes',
      'Implement performance improvements',
    ],
  },

  {
    id: 'NE-H3',
    itemId: 'H3',
    stateCode: 'NE',
    title: 'Complaint Hotline Awareness',
    description: 'Patient access to complaint procedures',
    severity: 'low',
    regulationCode: '175 NAC 14-010.05',
    regulationName: 'Complaints',
    agencyName: 'Nebraska Department of Health and Human Services',
    agencyAbbr: 'DHHS',
    agencyPhone: '(800) 245-5832',
    agencyUrl: 'https://dhhs.ne.gov/licensure/Pages/Home-Health-Agencies.aspx',
    questions: [
      {
        id: 'q1',
        question: 'Do patients receive information about how to file complaints with DHHS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - included in patient rights' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Nebraska Medicare Home Health Hotline: (800) 245-5832',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Complaint information is provided to patients.',
      atRisk: 'Include DHHS complaint hotline in patient rights: (800) 245-5832.',
      nonCompliant: 'Patients must be informed of right to file complaints.',
      unknown: 'Verify complaint information is provided.',
    },
    stateNotes: [
      'Nebraska Medicare Home Health Hotline: (800) 245-5832',
      'Include in patient rights notice',
      'Display Medicare Hotline Brochure (available from DHHS)',
    ],
  },
];

export default NEBRASKA_QUESTIONS;